import { useState } from "react";
import { useForm } from "react-hook-form";
import { formatDateT } from "../../../../shared/utils/formatters";
import NewDateTimePicker from "../../../../shared/components/newDateTimePicker";
import QuoteStep from "../../enums/QuoteStep";
import { fetchPanelData } from "../../services/quoteService";
import { handleNext } from "../../../../shared/utils/navigationUtils";
import useAppStateStore from "../../../../shared/store/AppStateStore";
import useQuoteStore from "../../store/QuoteStore";
import useBuyStore from "../../../buy/store/BuyStore";
import { fetchOptional, saveMember } from "../../../buy/services/buyService";
import BuyStep from "../../../buy/enums/BuyStep";
import { useNavigate } from "fusion-plugin-react-router";
import { TAXI_QUOTE_BRANDREF } from "../../../../config/config";
import ModalWrapper from "../../../../shared/components/ModalWrapper";
import Button from "../../../../shared/components/Button";

const TqPolicyDateTimeModal = () => {
  const {
    watch,
    setValue,
    control,
    handleSubmit,
    formState: { errors, isValid },
    trigger, // <-- get trigger from useForm
  } = useForm();
  const brandRef = TAXI_QUOTE_BRANDREF;
  const STATE = useAppStateStore();
  const navigate = useNavigate();
  const [startDate] = useState(
    useQuoteStore?.getState()?.quote?.risk?.startDate,
  );
  const [isEditMode, setIsEditMode] = useState(false);
  const [isDateLimitModalOpen, setIsDateLimitModalOpen] = useState(false);
  const onClose = () => {
    STATE.setIspolicyModal(false);
  };

  const handleOptionalFetch = async (STATE, navigate) => {
    const RiskObjectId = useQuoteStore.getState().quote?.risk?.riskObjectId;

    try {
      const jsonData = await fetchOptional({
        brandRef: brandRef,
        RiskObjectId: RiskObjectId,
        RiskObjectHistoryId: 1,
        navigate,
      });

      const buyStore = useBuyStore.getState();
      buyStore.setObject("recommendationsData", jsonData);
      setAddonFields(jsonData);
    } catch (error) {
      console.error("Error fetching recommendations data:", error);
    } finally {
      STATE.setRecommendationsLoading(false);
      STATE.setQuoteLoading(false);
    }
  };

  const handleYesClick = async () => {
    STATE.setQuoteLoading(true);
    STATE.setRecommendationsLoading(true);
    onClose();
    await saveMember(
      useBuyStore.getState().objects.panelMemberReqBody,
      navigate,
    );
    await handleOptionalFetch(STATE, navigate);
    handleNext(BuyStep.TB_ADDON_RECOMMENDATIONS);
  };

  const handleConfirmClick = () => {
    const { updateField } = useQuoteStore.getState();
    const newDate = formatDateT(watch("date"));
    updateField("risk.startDate", newDate);
    requote();
    onClose();
  };

  const requote = () => {
    const quoteData = useQuoteStore.getState().quote;
    fetchPanelData(
      navigate,
      null,
      quoteData.risk.riskObjectId,
      quoteData.risk.startDate,
    ).then(() => {
      handleNext(QuoteStep.TQ_PANEL, STATE);
    });
    onClose();
    handleNext(QuoteStep.TQ_PANEL_LOADER, STATE);
  };

  return (
    <>
      <ModalWrapper
        heading="Confirm Start Date"
        onClose={() => STATE.setIspolicyModal(false)}
      >
        <form onSubmit={handleSubmit(handleConfirmClick)}>
          {!isEditMode ? (
            <>
              <div className="tw-flex tw-flex-col tw-gap-2">
                <div className="tw-flex tw-items-center tw-justify-between tw-gap-4 tw-rounded-lg tw-border tw-border-solid tw-border-dis-border-strong tw-bg-dis-info-panel tw-px-4 tw-py-3">
                  <span className="tw-text-base tw-font-bold tw-text-dis-navy">
                    Cover start date
                  </span>
                  <span className="tw-text-base tw-text-dis-text-2">
                    {startDate
                      ?.split("T")[0]
                      .split("-")
                      .reverse()
                      .join("/")}
                  </span>
                </div>
                <div className="tw-flex tw-items-center tw-justify-between tw-gap-4 tw-rounded-lg tw-border tw-border-solid tw-border-dis-border-strong tw-bg-dis-info-panel tw-px-4 tw-py-3">
                  <span className="tw-text-base tw-font-bold tw-text-dis-navy">
                    Cover start time
                  </span>
                  <span className="tw-text-base tw-text-dis-text-2">
                    {startDate?.split("T")[1]}
                  </span>
                </div>
              </div>
              <div className="tw-mb-3 tw-mt-5 tw-text-base tw-leading-[1.5] tw-text-dis-text-2">
                Are you sure you would like your policy to start at the date and
                time you have selected?
              </div>
              <div className="tw-flex tw-justify-end tw-gap-3 max-[480px]:tw-flex-col">
                <Button
                  type="button"
                  variant="soft"
                  size="small"
                  onClick={() => setIsEditMode(true)}
                  className="max-[480px]:tw-w-full"
                >
                  Edit
                </Button>
                <Button
                  type="button"
                  variant="main"
                  size="small"
                  onClick={handleYesClick}
                  className="max-[480px]:tw-w-full"
                >
                  Yes
                </Button>
              </div>
            </>
          ) : (
            <>
              <div>
                <NewDateTimePicker
                  name="date"
                  label="When would you like your cover to start?"
                  errors={errors.date}
                  setValue={setValue}
                  control={control}
                  rules={{
                    required: "Date is required",
                  }}
                  watch={watch}
                  trigger={trigger}
                  defaultValue={watch("date")}
                  minDate={new Date()}
                  maxDate={
                    new Date(new Date().setDate(new Date().getDate() + 29))
                  }
                  onMaxDateExceeded={() => setIsDateLimitModalOpen(true)}
                />
                <div className="tw-mb-5 tw-mt-3 tw-rounded-xl tw-bg-dis-sky-tint tw-px-4 tw-py-2.5 tw-text-sm tw-leading-[1.45] tw-text-dis-navy">
                  A change in cover start date may result in a change in price.
                </div>
                <div className="tw-flex tw-justify-end tw-gap-3 max-[480px]:tw-flex-col">
                  <Button
                    type="button"
                    variant="soft"
                    size="small"
                    onClick={() => setIsEditMode(false)}
                    className="max-[480px]:tw-w-full"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="main"
                    size="small"
                    disabled={!isValid}
                    className="max-[480px]:tw-w-full"
                  >
                    Confirm
                  </Button>
                </div>
              </div>
            </>
          )}
        </form>
      </ModalWrapper>
      {isDateLimitModalOpen && (
        <ModalWrapper
          heading="Date not available"
          zIndex={1400}
          simpleText="You can only select a date for a quote up to 30 days in advance."
          buttonText="OK"
          onClose={() => setIsDateLimitModalOpen(false)}
          onButtonClick={() => setIsDateLimitModalOpen(false)}
        />
      )}
    </>
  );
};

export default TqPolicyDateTimeModal;
